// Canonical verdict engine for Timbo Performance.
// Single source of truth — replaces verdictEngine.js (live) and classifier.js (dead).
// v2 (22 Sep 2026, UA regime): testing = EN adsets, verdict by NUMBER OF PURCHASES per run
// (>=5 = winner -> push BAU · 1-4 = retest handled by Violeta · 0 = cut). NO CPA gate in test —
// CPA is judged in BAU (main phase). The Meta cap now only feeds the informational `truncated` flag.
// main = US, CPA reliable, unchanged for now (fatigue.cpaAbove to recalibrate on real BAU CPA).
// Thresholds persisted in localStorage 'timbo_perf_rules_v2' (shared via workspace if whitelisted).
import { parseNomenclature } from './nomenclatureParser.js';

export const DEFAULT_RULES = {
  ad_spend_cap: 65,           // €/ad Meta cap. Feeds ONLY the `truncated` flag (display), never the test verdict.
  cap_truncation_frac: 0.9,   // spend >= frac*cap with few conv => CPA truncated/unreliable (info only)
  // v2 UA regime: test verdict = purchase counter per run, never CPA.
  testing: {
    winner:  { minConv: 5 },               // >=5 purchases in ONE run (one adset) => push BAU
    retest:  { minConv: 1, maxConv: 4 },   // 1-4 purchases => retest handled by Violeta (info only)
    dead:    { minSpend: 50, minDays: 2 }, // 0 purchase + enough spend & time => CUT
    runDone: { minSpend: 50, minDays: 3 }, // run considered finished (to calibrate)
  },
  main: {
    fatigue: { cpaAbove: 50 },                 // US main — À CALIBRER avec tes vrais CPA US
    iterate: { maxCPA: 40, minSpend: 500 },
  },
  localize: { minSpend: 2000, lang: 'en' },    // 2000 preserves current live LOCALIZE counts
};

// v2: new key. Old CPA rules (minConv 3, maxCPA 35) saved under 'timbo_perf_rules' would
// otherwise override the purchase regime via loadRules' merge. Start from a clean key;
// the old one is ignored (clients still on v1 keep reading theirs until the deploy).
const STORAGE_KEY = 'timbo_perf_rules_v2';

export function loadRules() {
  try {
    const s = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    // only accept v2-shaped rules (testing.winner present, purchase regime)
    if (s && s.testing && s.testing.winner && s.main && s.localize) {
      return { ...DEFAULT_RULES, ...s };
    }
  } catch (e) { /* fall through */ }
  return JSON.parse(JSON.stringify(DEFAULT_RULES));
}

export function saveRules(rules) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(rules)); } catch (e) { /* ignore */ }
}

// Sort order used by PerformanceTab (moved here from the retired classifier.js).
export const ACTION_PRIORITY = {
  testing: { PROMOTE: 1, RETEST: 2, WAIT: 3, CUT: 4 },
  main:    { ITERATE: 1, LOCALIZE: 2, HEALTHY: 3, FATIGUE: 4, CUT: 5 },
};

const r0 = (n) => Math.round(n);

function daysLiveOf(ad) {
  if (!ad.first_date) return 0;
  const start = new Date(ad.first_date);
  const end = ad.last_date ? new Date(ad.last_date) : new Date();
  return Math.max(0, Math.round((end - start) / 86400000));
}

// Leading indicators from video quartiles (populated by Bloc 3; degrade gracefully to null).
export function leadingMetrics(ad) {
  const imp = ad.impressions || 0;
  const hookRate = imp > 0 && ad.video_p25 != null ? (ad.video_p25 / imp) * 100 : null;
  const holdRate = imp > 0 && ad.video_p100 != null ? (ad.video_p100 / imp) * 100 : null;
  const ctr = ad.ctr != null ? ad.ctr
            : (imp > 0 && ad.clicks != null ? (ad.clicks / imp) * 100 : null);
  return { hookRate, holdRate, ctr };
}

/**
 * Single verdict function. Returns { action, reason, truncated, cpaReliable }.
 * opts: { phase: 'testing'|'main'|'other', isPromoted?: bool, lang?: string }
 */
export function getAction(ad, rules, opts = {}) {
  const r = rules || DEFAULT_RULES;
  const phase = opts.phase || 'other';
  const lang = opts.lang || null;

  const conv = ad.achats || 0;
  const spend = ad.spend || 0;
  const cpa = (ad.cpa != null) ? ad.cpa : (conv > 0 ? spend / conv : null);
  const daysLive = daysLiveOf(ad);

  // CPA reliability: near the per-ad cap with few conversions => CPA is truncated (info only).
  const cap = r.ad_spend_cap || 65;
  const nearCap = spend >= (r.cap_truncation_frac || 0.9) * cap;
  const truncated = nearCap && conv > 0 && conv < (r.testing.winner.minConv || 3);
  const cpaReliable = !truncated;

  if (phase === 'testing') {
    // v2 UA regime: judge ONLY the number of purchases per run. No CPA in test
    // (CPA is judged in BAU). `truncated` stays computed for display, never for the verdict.
    const runDone = spend >= r.testing.runDone.minSpend || daysLive >= r.testing.runDone.minDays;
    if (conv >= r.testing.winner.minConv) {
      return mk('PROMOTE', `${conv} conv · €${r0(spend)} · ${daysLive}d`, truncated, cpaReliable);
    }
    if (conv >= r.testing.retest.minConv && conv <= r.testing.retest.maxConv && runDone) {
      return mk('RETEST', `${conv} conv · retest (Violeta)`, truncated, cpaReliable);
    }
    if (conv === 0 && spend >= r.testing.dead.minSpend && daysLive >= r.testing.dead.minDays) {
      return mk('CUT', `0 conv · €${r0(spend)} · ${daysLive}d`, truncated, cpaReliable);
    }
    return mk('WAIT', `${conv} conv · €${r0(spend)} · ${daysLive}d · run en cours`, truncated, cpaReliable);
  }

  if (phase === 'main') {
    if (lang === r.localize.lang && spend >= r.localize.minSpend
        && cpa != null && cpa <= r.main.iterate.maxCPA) {
      return mk('LOCALIZE', `€${(spend/1000).toFixed(1)}k · CPA €${r0(cpa)} · ready`, truncated, cpaReliable);
    }
    if (cpa != null && cpa > r.main.fatigue.cpaAbove && spend >= 200) {
      return mk('FATIGUE', `CPA €${r0(cpa)} > €${r.main.fatigue.cpaAbove}`, truncated, cpaReliable);
    }
    if (cpa != null && cpa <= r.main.iterate.maxCPA && spend >= r.main.iterate.minSpend) {
      return mk('ITERATE', `CPA €${r0(cpa)} · ${conv} conv · remix`, truncated, cpaReliable);
    }
    return mk('HEALTHY', cpa != null ? `CPA €${r0(cpa)}` : `€${r0(spend)}`, truncated, cpaReliable);
  }

  return mk('WAIT', 'no phase', truncated, cpaReliable);
}

function mk(action, reason, truncated, cpaReliable) {
  return { action, reason, truncated, cpaReliable };
}

// Start of the v2 UA regime (Violeta sync). Winners are tagged against this date.
export const RULES_V2_START = '2026-09-22';

// Frozen copy of the v1 test-winner rule (in force before RULES_V2_START). Used ONLY to tag
// LEGACY winners in the Winners card — never for a live verdict.
const V1_TEST_RULE = {
  ad_spend_cap: 65, cap_truncation_frac: 0.9,
  winner: { maxCPA: 35, minConv: 3, minSpend: 80 },
  dead: { minSpend: 50, minDays: 2 },
  minCTR: 0.8,
};

// v1 testing PROMOTE logic, verbatim (incl. the capped-but-converting CTR path).
function passedV1TestRule(ad) {
  const r = V1_TEST_RULE;
  const conv = ad.achats || 0;
  const spend = ad.spend || 0;
  const cpa = (ad.cpa != null) ? ad.cpa : (conv > 0 ? spend / conv : null);
  const { ctr } = leadingMetrics(ad);
  const truncated = spend >= r.cap_truncation_frac * r.ad_spend_cap && conv > 0 && conv < r.winner.minConv;
  if (spend >= r.dead.minSpend && conv === 0 && daysLiveOf(ad) >= r.dead.minDays) return false;
  if (truncated) return !(ctr != null && ctr < r.minCTR);
  if (cpa != null && cpa > r.winner.maxCPA * 2.5 && spend >= r.dead.minSpend) return false;
  return cpa != null && cpa <= r.winner.maxCPA && conv >= r.winner.minConv && spend >= r.winner.minSpend;
}

// Date of a run: its launch day (first_date), so a long-tail delivery day can't move an old
// test into the new regime.
const runDateOf = (ad) => ad.first_date || ad.last_date || null;

const moreConv = (best, a) => (best && (best.achats || 0) >= (a.achats || 0)) ? best : a;

/**
 * Every creative not in Main whose best TEST run qualifies, tagged by the rule it meets:
 *   NEW         — run on/after RULES_V2_START, passes today's rule (>=5 purchases in that run)
 *   STILL_VALID — run before RULES_V2_START, but passes today's rule
 *   LEGACY      — run before RULES_V2_START, only passed the v1 rule
 * Counted per run (one TEST adset), never summed across a test and its retest.
 * Sorted by qualifying run date, newest first. Adds { winner_tag, qualifying_date }.
 */
export function findTaggedWinners(aggregatedAds, rules) {
  const r = rules || loadRules();
  const byCoreId = new Map();
  for (const ad of aggregatedAds) {
    if (!ad.core_id) continue;
    if (!byCoreId.has(ad.core_id)) byCoreId.set(ad.core_id, []);
    byCoreId.get(ad.core_id).push(ad);
  }
  const out = [];
  for (const ads of byCoreId.values()) {
    if (ads.some(a => ['MAIN', 'BIS', 'ITERATION'].includes(a.adset_category))) continue;
    // v2: per-RUN threshold. The aggregator keys TEST rows per adset
    // (csvParserV2.aggregateByCorIdAndCategory), so a test and its retest are separate rows.
    // Judge the BEST run (max purchases), never the sum nor an arbitrary run.
    const testAds = ads.filter(a => a.adset_category === 'TEST');
    const testAd = testAds.reduce(moreConv, null);
    if (!testAd) continue;
    const lang = parseNomenclature(testAd.ad_name_sample).lang;
    const { action } = getAction(testAd, r, { phase: 'testing', lang });
    if (action === 'PROMOTE') {
      const d = runDateOf(testAd);
      const tag = d && d >= RULES_V2_START ? 'NEW' : 'STILL_VALID';
      out.push({ ...testAd, action, winner_tag: tag, qualifying_date: d });
      continue;
    }
    // Legacy: a run from the v1 era that met the v1 rule (and not today's).
    const legacyRun = testAds
      .filter(a => { const d = runDateOf(a); return d && d < RULES_V2_START && passedV1TestRule(a); })
      .reduce(moreConv, null);
    if (legacyRun) out.push({ ...legacyRun, action, winner_tag: 'LEGACY', qualifying_date: runDateOf(legacyRun) });
  }
  return out.sort((a, b) => (b.qualifying_date || '').localeCompare(a.qualifying_date || ''));
}

// TEST ads judged PROMOTE under today's rule that have not yet been pushed to a scaling adset
// (= NEW + STILL_VALID). Sorted by purchases.
export function findWinnersToPromote(aggregatedAds, rules) {
  return findTaggedWinners(aggregatedAds, rules)
    .filter(w => w.winner_tag !== 'LEGACY')
    .sort((a, b) => (b.achats || 0) - (a.achats || 0));
}
